import React, { useState, useEffect, useContext } from 'react';

import { Alert, message, Tooltip } from 'antd';
import { DatePicker } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';

import styled from 'styled-components';

import { isBookingEvent } from '@libs/EventTypes';
import { getMoment, showDateNoTime, toISODate } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import { useContact, name } from '@hooks/API/Contact/UseContact';

import CustomSwitch from '@components/Gui/CustomSwitch';
import { Loading } from '@components/Gui/Loading';
import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWInput, NWSignatureSelector, NWTextAreaCharCounter } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import StyleVariables from '@components/Gui/StyleVariables';
import { getSendAgendaRange } from '@components/Schedule/ScheduleUtils';
import Attachments from '@components/SendAgendaDrawer/Attachments';
import Recipients from '@components/SendAgendaDrawer/Recipients';
import SendAgendaMailPreview from '@components/SendAgendaDrawer/SendAgendaMailPreview';
import SelectTemplateSelector from '@components/TemplateSelector/SelectTemplateSelector';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { RangePicker } = DatePicker;

const Wrapper = styled.div`
    .ant-legacy-form-item {
        margin-bottom: 8px;
    }

    .mobile-form-field {
        margin-bottom: 1rem;

        .mobile-form-label {
            font-size: ${(props) => props.theme.FontSizeSm};
            color: ${(props) => props.theme.TextColorMuted};
            text-transform: uppercase;
            margin-bottom: 0.25rem;
        }

        .mobile-form-value {
            .ant-form-item {
                margin-bottom: 0;
            }
            .nw-form-item-container {
                width: 100%;
            }
        }
    }

    .mobile-form-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 1rem;
    }

    .mobile-preview-container {
        [data-name='content'] {
            zoom: 0.54;
        }
    }
`;

const LoadingContainer = styled.div`
    position: absolute;
    right: 0;
    left: 0;
    top: 40%;
    z-index: 2000;
`;

const DrawerContent = styled.div`
    opacity: ${(props) => props.isSending && 0.4};
`;

const SendAgendaDrawerMobile = ({ scheduleRange, contactID, warning, onClose }) => {
    const [docVersion, setDocVersion] = useState();
    const [attachments, setAttachments] = useState([]);
    const [docVersionID, setDocVersionID] = useState(0);
    const loading = false;
    const [isSending, setIsSending] = useState(false);
    const [recipientsData, setRecipientsData] = useState([]);
    const { data: currentContact } = useContact(contactID);
    const [docversionHtml, setDocversionHtml] = useState('');
    const [eventDateMap, setEventDateMap] = useState({});
    const [acknowledged, setAcknowledged] = useState(true);
    const [touchForm, setTouchForm] = useState(false);
    const [title, setTitle] = useState('');
    const [selectedSignature, setSelectedSignature] = useState(null);
    const [errorSending, setErrorSending] = useState(null);
    const { templates } = useContext(ListContext).cachedList;
    const calendarTemplates = templates.items.filter((item) => item.area === 'Calendar');
    // Starts from the tenant's default Calendar template
    const [template, setTemplate] = useState(
        () => calendarTemplates.find((item) => item.isDefault) || calendarTemplates[0]
    );

    const sendAgendaRange = getSendAgendaRange(scheduleRange);

    const [filters, setFilters] = useState({
        ExcludeAcknowledged: true,
        ExcludeCancelled: true,
        ExcludedEvents: [],
        ExcludeOptions: true,
        FDate: sendAgendaRange.start,
        InclAccommodations: true,
        InclCastings: true,
        InclExternalJobs: true,
        InclFollowUps: true,
        InclJobs: true,
        InclExtJobs: true,
        InclMeetings: false,
        InclNotices: true,
        InclTravels: true,
        IncludedEventSubTypes: [],
        ModelID: contactID,
        TDate: sendAgendaRange.end,
    });

    useEffect(() => {
        if (filters.FDate && filters.TDate && currentContact) {
            let tp = `Schedule for ${name(currentContact)}, ${showDateNoTime(filters.FDate)}`;
            if (getMoment(filters.TDate).isAfter(getMoment(filters.FDate), 'day')) {
                tp += ` - ${showDateNoTime(filters.TDate)}`;
            }
            setTitle(tp);
            onCreateDocversion();
        }
    }, [filters, currentContact, template]);

    const onCreateDocversion = async () => {
        try {
            const filtersToPost = { ...filters };
            if (docVersionID) {
                filtersToPost.UpdateDocVersionID = docVersionID;
            }
            // cha1 is the standard Calendar template, kept as fallback when the tenant list has none
            const templateID = template ? template.id : 'cha1';
            const docversionresponse = await Axios.post(
                `/calendar/ModelDocVersion?area=Calendar&template=${templateID}`,
                filtersToPost
            );
            if (!docVersionID) {
                setDocVersionID(docversionresponse.Docversion.ID);
            }
            setDocVersion(docversionresponse.Docversion);
            if (docversionresponse.Attachments) {
                const attachmentsWithIncludeProperty = docversionresponse.Attachments.map((attachment) => ({
                    ...attachment,
                    Include: attachment.IsHidden ? false : true,
                }));
                setAttachments(attachmentsWithIncludeProperty);
            }
        } catch (error) {
            console.error('Create Docversion Error => ', error);
        }
    };

    const initialValues = {
        Subject: title,
        Description: '',
        SignatureID: null,
    };

    const handleUpdateRecipientsData = (newRecipientsData) => {
        setRecipientsData(newRecipientsData);
    };

    const handleChangeAcknowledged = (acknowledged) => {
        setAcknowledged(acknowledged);
    };

    const handleChangePeriod = (period) => {
        setFilters({
            ...filters,
            FDate: toISODate(period[0]),
            TDate: toISODate(period[1]),
        });
    };

    const onSendAgenda = async (values, setSubmitting) => {
        setErrorSending(null);
        if (docVersion) {
            let AcknowledgeOnSend = [];
            if (acknowledged) {
                let events = {};
                for (const eventDate of Object.keys(eventDateMap)) {
                    for (const event of eventDateMap[eventDate]) {
                        if (!events[event.eventType]) {
                            events[event.eventType] = isBookingEvent(event.eventType)
                                ? [event.eventdatemodelid]
                                : [event.eventId];
                        } else {
                            events[event.eventType].push(
                                isBookingEvent(event.eventType) ? event.eventdatemodelid : event.eventId
                            );
                        }
                    }
                }

                let eventsMap = {};
                for (const eventType of Object.keys(events)) {
                    if (isBookingEvent(eventType)) {
                        eventsMap.CheckEvents = eventsMap.CheckEvents
                            ? [...eventsMap.CheckEvents, ...events[eventType]]
                            : [...events[eventType]];
                    } else if (eventType.toLowerCase() === 'travel') {
                        eventsMap.CheckTravels = [...events[eventType]];
                    } else if (eventType.toLowerCase() === 'followup') {
                        eventsMap.CheckFollowUps = [...events[eventType]];
                    }
                }

                AcknowledgeOnSend = Object.keys(eventsMap).map((eventType) => {
                    return {
                        Action: eventType,
                        Data: { Events: eventsMap[eventType] },
                    };
                });
            }

            const DocumentsToAttach =
                attachments && attachments.length > 0
                    ? attachments.filter((attachment) => attachment.Include).map((attachment) => attachment.ID)
                    : [];

            const data = [
                {
                    DocVersionGUID: docVersion.DocGuid,
                    SendMethod: 'SendGrid',
                    Subject: values.Subject,
                    AsAttachment: false,
                    Message: values.Description,
                    SignatureID: values.SignatureID,
                    DocumentsToAttach,
                    ActionsBefore: AcknowledgeOnSend,
                },
            ];

            data[0].Recipients = [...recipientsData];

            setSubmitting(true);
            setIsSending(true);

            try {
                const htmlContent = docVersion.Html;

                let replacedhtml = htmlContent;
                replacedhtml = replacedhtml.replace('{{Title}}', values.Subject);
                replacedhtml = replacedhtml.replace('{{Message}}', values.Description);

                await Axios.put('/docVersions/UpdateHtml', {
                    ID: docVersion.ID,
                    Html: replacedhtml,
                });

                await Axios.post('/docVersions/sendmessage', data);
                setTouchForm(false);
                setSubmitting(false);
                setIsSending(false);
                onClose();
            } catch (error) {
                setSubmitting(false);
                setIsSending(false);
                setErrorSending('There was an error sending the message');
                console.error('Send Agenda Error => ', error);
            }
        }
    };

    const SendButton = ({ values, isSubmitting }) => {
        let invalidMessage = '';
        if (recipientsData.length === 0) {
            invalidMessage += 'Please select at least one recipient';
        }
        if (!values.Subject) {
            invalidMessage +=
                invalidMessage.length > 0
                    ? ' and enter a subject line for the e-mail'
                    : 'Please write a subject line for the e-mail';
        }
        if (invalidMessage.length > 0) {
            return (
                <Tooltip
                    placement="left"
                    title={invalidMessage}
                    color={StyleVariables.DangerColor}
                    overlayInnerStyle={{ fontSize: StyleVariables.FontSizeSm, maxWidth: '20rem' }}
                >
                    <NwSaveButton htmlType="submit" icon={byPrefixAndName.fasl['paper-plane']} label="Send" disabled />
                </Tooltip>
            );
        }
        return (
            <NwSaveButton
                htmlType="submit"
                icon={byPrefixAndName.fasl['paper-plane']}
                label="Send"
                disabled={isSubmitting}
                loading={isSubmitting}
            />
        );
    };

    return (
        <NwDrawer
            touchForm={touchForm}
            onTouchForm={setTouchForm}
            title="Send Agenda"
            onClose={onClose}
            width="100vw"
            maxWidth="100vw"
            id="send-agenda-drawer-mobile"
        >
            {!currentContact && loading ? (
                <Loading />
            ) : (
                <>
                    {isSending && (
                        <LoadingContainer>
                            <Loading />
                        </LoadingContainer>
                    )}
                    <DrawerContent isSending={isSending}>
                        <Wrapper>
                            <Formik
                                initialValues={initialValues}
                                onSubmit={(values, { setSubmitting }) => {
                                    onSendAgenda(values, setSubmitting);
                                }}
                            >
                                {({ isSubmitting, values, setFieldValue, handleSubmit }) => {
                                    useEffect(() => {
                                        setFieldValue('Subject', title);
                                    }, [title]);

                                    return (
                                        <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit}>
                                            <div className="mobile-form-field">
                                                <div className="mobile-form-label">Period</div>
                                                <div className="mobile-form-value">
                                                    <RangePicker
                                                        autoComplete="off"
                                                        format="D MMM YYYY"
                                                        style={{ width: '100%' }}
                                                        value={[getMoment(filters.FDate), getMoment(filters.TDate)]}
                                                        onChange={handleChangePeriod}
                                                    />
                                                </div>
                                            </div>
                                            <div className="mobile-form-field">
                                                <CustomSwitch
                                                    checked={acknowledged}
                                                    onChange={handleChangeAcknowledged}
                                                    text="Set events as acknowledged"
                                                />
                                            </div>
                                            {calendarTemplates.length > 1 && (
                                                <div className="mobile-form-field">
                                                    <div className="mobile-form-label">Template</div>
                                                    <div className="mobile-form-value">
                                                        <SelectTemplateSelector
                                                            type="Calendar"
                                                            template={template}
                                                            style={{ width: '100%' }}
                                                            onTemplateChange={setTemplate}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            <div className="mobile-form-field">
                                                <div className="mobile-form-label">E-mail Subject</div>
                                                <div className="mobile-form-value">
                                                    <Field
                                                        component={NWInput}
                                                        name="Subject"
                                                        type="text"
                                                        placeholder="Subject"
                                                        id="Subject"
                                                        value={values.Subject}
                                                    />
                                                </div>
                                            </div>
                                            <div className="mobile-form-field">
                                                <div className="mobile-form-label">Message</div>
                                                <div className="mobile-form-value">
                                                    <Field
                                                        component={NWTextAreaCharCounter}
                                                        name="Description"
                                                        type="text"
                                                        placeholder="Description"
                                                        value={values.Description}
                                                        autoSize={{ minRows: 2, maxRows: 4 }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="mobile-form-field">
                                                <div className="mobile-form-label">Signature</div>
                                                <div className="mobile-form-value">
                                                    <Field
                                                        component={NWSignatureSelector}
                                                        name="SignatureID"
                                                        value={values.SignatureID}
                                                        onSelectSignature={(value) => setSelectedSignature(value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="mobile-preview-container">
                                                {docVersion ? (
                                                    <SendAgendaMailPreview
                                                        twoColumns={false}
                                                        mobile
                                                        docVersionID={docVersion.ID}
                                                        htmlContent={docVersion.Html}
                                                        message={values.Description}
                                                        signature={selectedSignature}
                                                        title={values.Subject}
                                                        eventDateMap={eventDateMap}
                                                        filters={filters}
                                                        onUpdateFilters={setFilters}
                                                        onUpdateDocversion={setDocversionHtml}
                                                        onUpdateEventDateMap={setEventDateMap}
                                                    />
                                                ) : (
                                                    <Loading textBlack />
                                                )}
                                            </div>
                                            <Recipients
                                                contactID={contactID}
                                                recipientsData={recipientsData}
                                                onUpdateRecipientsData={handleUpdateRecipientsData}
                                            />
                                            {attachments && attachments.length > 0 && (
                                                <Attachments
                                                    modelId={contactID}
                                                    attachments={attachments}
                                                    onUpdateAttachments={setAttachments}
                                                />
                                            )}
                                            {errorSending && (
                                                <Alert
                                                    style={{ marginTop: '1rem' }}
                                                    message="Error"
                                                    description={errorSending}
                                                    type="error"
                                                    showIcon
                                                />
                                            )}
                                            <NwFormButtonsBar
                                                marginTop="2rem"
                                                left={
                                                    <NwCancelButton
                                                        disabled={isSubmitting}
                                                        onClick={() => {
                                                            setTouchForm(false);
                                                            onClose();
                                                        }}
                                                    />
                                                }
                                                right={<SendButton values={values} isSubmitting={isSubmitting} />}
                                            />
                                        </NwForm>
                                    );
                                }}
                            </Formik>
                        </Wrapper>
                    </DrawerContent>
                </>
            )}
        </NwDrawer>
    );
};

export default SendAgendaDrawerMobile;
